<template>
    <div class="hle-approval-review-page boq-approval-review-page">
        <BreadcrumbsProject
            v-if="!isDeluxeLayout"
            :breadcrumbs="[
                {
                    link : dotnetUrl +'/project/list',
                    title : $t('Projects'),
                    isRouterLink: false
                },
                {
                    link : projectUrl +'/Project/' + projectInfo._id,
                    title :  (projectInfo.projectNumber || '-') +' ' + (projectInfo.projectName || ''),
                    isRouterLink: false
                },
                {
                    link : '/boq/' + projectInfo._id,
                    title : $t('Bill of Quantities'),
                    isRouterLink: true
                },
                {
                    link : '/boq/builder/' + processItemData.relatedId,
                    title : (boqItemData.name || '-') ,
                    isRouterLink: true
                },
                {
                    link : '/boq/review/' + processItemData._id,
                    title : $t('Approval Module.Approval') ,
                    isRouterLink: true
                },
            ]"
        />

        <button type="button" class="boq-approval-setup-back hle-approval-review-back" @click="BackToItem">
            <i class="fa-solid fa-chevron-left"></i>
            <span>{{ $t('Back') }}</span>
        </button>

        <section class="hle-approval-review-panel">
            <header class="hle-approval-review-hero">
                <div class="hle-approval-review-hero__main">
                    <span class="hle-approval-review-eyebrow">{{ $t('Approval Module.Approval of') }}</span>
                    <h1>{{ boqItemData.name || '-' }}</h1>
                    <router-link
                        v-if="boqItemData._id"
                        :to="{name : 'BoqBuilder', params : {id : boqItemData._id} }"
                        class="hle-approval-review-reference"
                    >
                        <i class="fa-solid fa-up-right-from-square"></i>
                        <span>{{ $t('Bill of Quantities') }}</span>
                    </router-link>
                </div>

                <div class="hle-approval-review-hero__side">
                    <span :class="'hle-bidding-status-pill ' + ApprovalStatusClass">{{ ApprovalStatusLabel }}</span>
                    <dl class="hle-approval-review-dates">
                        <div>
                            <dt>{{ $t('Approval Module.Sent Date') }}</dt>
                            <dd>{{ formatDate(processItemData.createdAt) || '-' }}</dd>
                        </div>
                        <div>
                            <dt>{{ $t('Approval Module.Expected End Date') }}</dt>
                            <dd>{{ formatDate(processItemData.dateEnd) || '-' }}</dd>
                        </div>
                    </dl>
                </div>
            </header>

            <div class="hle-approval-review-workflow">
                <BoqProcessWorkFlow
                    v-if="processItemData._id"
                    :processItemData="processItemData"
                    :showApprovalDisapprove="true"
                />
            </div>

            <div class="hle-approval-review-summary">
                <div class="hle-approval-review-report-head">
                    <div class="hle-approval-review-report-field">
                        <span>{{ $t('Project') }}</span>
                        <strong>{{ ProjectLabel }}</strong>
                    </div>
                    <div class="hle-approval-review-report-field">
                        <span>{{ $t('Version') }}</span>
                        <strong>{{ VersionLabel }}</strong>
                    </div>
                    <div class="hle-approval-review-report-field">
                        <span>{{ $t('Currency') }}</span>
                        <strong>{{ CurrencyLabel }}</strong>
                    </div>
                    <div class="hle-approval-review-report-field">
                        <span>{{ $t('Approval Module.Done by') }}</span>
                        <strong>{{ DoneByLabel }}</strong>
                    </div>
                </div>

                <FigureCurrencyNote :currency="boqItemData.currencyId" />

                <div class="hle-approval-review-table">
                    <BoqSectionTableReadOnly
                        :rawSections="rawSections"
                        :boqItem="boqItemData"
                        :approvalId="processItemData._id"
                        :breakdownLink="breakdownLink"
                    />
                </div>
            </div>
        </section>

        <div class="hle-approval-review-comments">
            <ProcessCommentList
                :PageChangeComment="PageChangeComment"
                :processItem="processItemData"
                :commentList="commentList"
            />
        </div>

        <div v-if="CanApprove">
            <div class="hle-approval-review-action-spacer"></div>
            <div class="hle-approval-review-action-dock">
                <button class="btn hle-approval-review-action hle-approval-review-action--approve" data-bs-toggle="modal" data-bs-target="#BoqReviewApprove">
                    <i class="fa-solid fa-thumbs-up"></i>
                    <span>{{$t('Approval Module.Approve')}}</span>
                </button>
                <button class="btn hle-approval-review-action hle-approval-review-action--revision" data-bs-toggle="modal" data-bs-target="#BoqReviewReject">
                    <i class="fa-solid fa-thumbs-down"></i>
                    <span>{{$t('Approval Module.Ask For Revision')}}</span>
                </button>
            </div>
        </div>

        <Modal idModal="BoqReviewApprove" :headerTitle="$t('Approval Module.Approve')">
            <div>
                <div class="form-group">
                    <label>{{ $t('Approval Module.Notes (optional)') }}: </label>
                    <textarea v-model="approveNotes" class="form-control"></textarea>
                </div>
            </div>
            <template v-slot:actions>
                <button type="button" ref="closeBoqReviewApprove" class="btn btn-outline-danger" data-bs-dismiss="modal">X {{$t('Cancel')}}</button>
                <button type="button" class="btn btn-primary" @click="Approve">
                    <i class="fas fa-thumbs-up"></i> {{$t('Approve')}}
                </button>
            </template>
        </Modal>

        <Modal idModal="BoqReviewReject" :headerTitle="$t('Approval Module.Ask For Revision')">
            <div>
                <div class="form-group">
                    <label>{{$t('Approval Module.Notes')}} <span class="bidding-required-inline" v-if="!rejectNotes">({{ $t('Required').toLowerCase() }})</span></label>
                    <textarea v-model="rejectNotes" class="form-control"></textarea>
                </div>
            </div>
            <template v-slot:actions>
                <button type="button" ref="closeBoqReviewReject" class="btn btn-outline-danger" data-bs-dismiss="modal">X {{$t('Cancel')}}</button>
                <button type="button" class="btn btn-primary" @click="Disapprove">
                    <i class="fas fa-thumbs-down"></i> {{$t('Approval Module.Ask For Revision')}}
                </button>
            </template>
        </Modal>
    </div>
</template>

<script src="@/assets/boq/BoqReview.js" />
